import { OTRSelect } from '../components/OTRSelect';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout, PageBackLink } from '../../../components/layout';
import {
  OTRStatusBadge,
  OTRApprovalActionBar,
  ExpenseHeadTable,
  DailyExpenseDashboard
} from '../components';
import {
  deriveOTRPermissions,
  calculateTripDays
} from '../utils/otrCalculations';
import {
  getTravelRequestById,
  getExpensePoliciesByTier,
  getTravelPolicySettings,
  approveTravelRequest,
  rejectTravelRequest,
  sendBackTravelRequest
} from '../../../services/outstationTravelService';
import {
  getCompanyArrangeableHeads,
  getEligibleExpenseHeads,
  normalizeArrangedMap,
} from '../utils/companyArranged';
import { errMsg } from '../utils/errorMessage';
import { isRequestApproved, canAddExpenseLines, canEditTravelRequest, isExpenseStageSentBack, EXPENSE_TABS, EXPENSE_LOCK_MESSAGE } from '../utils/otrCalculations';
import { OTR_STATUS, getModeOfTravelName, PURPOSE_I18N_KEYS } from '../constants/otrEnums';
import sessionDB from '../../../utils/sessionDB';
import { formatOtrDate } from '../utils/formatOtrDate';

const cx = (...c) => c.filter(Boolean).join(' ');

const OTR_TABS = [
  { key: 'travelInfo', labelKey: 'outstation.travelInfo' },
  { key: 'dailyExpense', labelKey: 'outstation.dailyExpenseEntry' },
];



export const OTRDetailPage = () => {
  const { otrId, id } = useParams();
  const requestId = otrId || id;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [request, setRequest] = useState(null);
  const [policyHeads, setPolicyHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [actionError, setActionError] = useState('');
  const [activeTab, setActiveTab] = useState('travelInfo');

  // Approval authorization is intentionally not decided in the browser.
  // The backend resolves the authenticated employee/role and validates the
  // current approval stage.
  const currentUserRole = '';

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getTravelRequestById(requestId);

        // approvalFlow is tenant policy config; use it if the request already
        // carries it, otherwise resolve it from the policy settings.
        let approvalFlow = data.approvalFlow;

        if (!approvalFlow) {
          try {
            const settings = await getTravelPolicySettings();
            approvalFlow = settings?.approvalFlow;
          } catch (err) {
            console.error(
              'Failed to load travel policy settings:',
              err
            );
          }
        }

        setRequest({
          ...data,
          approvalFlow
        });

        // Do not make the policy call when tenant/tier information
        // is not available in the backend response.
        if (data.tenantId && data.tierId) {
          try {
            const policies = await getExpensePoliciesByTier(
              data.tenantId,
              data.tierId
            );

            setPolicyHeads(
              Array.isArray(policies)
                ? policies
                : []
            );
          } catch (err) {
            console.error(
              'Failed to load expense policies:',
              err
            );

            setPolicyHeads([]);
          }
        } else {
          setPolicyHeads([]);
        }
      } catch (err) {
        console.error(
          'Failed to load travel request:',
          err
        );
        setActionError(errMsg(err, 'Could not load this travel request.'));
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [requestId]);

  if (loading) {
    return (
      <AppLayout>
        <div className="py-20 text-center">
          Loading...
        </div>
      </AppLayout>
    );
  }

  if (!request) {
    return (
      <AppLayout>
        <div className="py-20 text-center">
          Request not found.
        </div>
      </AppLayout>
    );
  }

  // Backend-first: use explicit capabilities when supplied; otherwise the
  // status-based fallback exposes the action and the backend authorizes it.
  const isOwner = Number(request.empId) === Number(sessionDB.getEmpId());

  const derivedPermissions = deriveOTRPermissions(
    request,
    currentUserRole
  );

  // The requester can never approve / reject / send back their own request.
  const permissions = isOwner
    ? {
        ...derivedPermissions,
        canApprove: false,
        canReject: false,
        canSendBack: false,
        canEditCityTier: false,
        canEditAdvance: false,
      }
    : derivedPermissions;

  const expensesUnlocked = isRequestApproved(request.status, request.sentBackFrom);

  const tripDays = calculateTripDays(
    request.dateOfTravel,
    request.dateOfReturn
  );

  const handleAction = async (actionFn, remarks) => {
    setProcessing(true);
    setActionError('');

    try {
      await actionFn(requestId, remarks);
      navigate('/outstation-travel/report');
    } catch (err) {
      console.error(err);
      setActionError(
        `${err?.status ? `(${err.status}) ` : ''}${err?.message || 'Action failed. Please try again.'}`
      );
    } finally {
      setProcessing(false);
    }
  };

  // Once expenses are submitted, approval happens from the Daily Expense Entry tab only.
  const isExpenseStage = [
    OTR_STATUS.EXPENSES_SUBMITTED,
    OTR_STATUS.EXPENSES_L1_APPROVED,
  ].includes(request.status);

  const showApprovalBar =
    (permissions.canApprove || permissions.canReject || permissions.canSendBack) &&
    (!isExpenseStage || activeTab === 'dailyExpense');

  return (
    <AppLayout>
      <PageBackLink
        to={isOwner ? '/outstation-travel' : '/outstation-travel/report'}
        label={isOwner ? t('outstation.myTravelRequests') : t('outstation.outstationReport')}
      />

      {/* Header bar */}
      <div className="p-4 sm:p-6 bg-white rounded-2xl border border-[var(--color-border)] shadow-sm flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-lg font-bold text-foreground leading-tight">
              {request.requestRef || `OTR-${request.travelRequestId}`}
            </h1>

            <OTRStatusBadge
              status={request.status}
            />
          </div>

          <p className="text-sm text-[var(--color-text-secondary)]">
            {request.placeOfTravel || '—'}
            {' · '}
            {formatOtrDate(request.dateOfTravel)}
            {` ${t('outstation.dateRangeTo')} `}
            {formatOtrDate(request.dateOfReturn)}
            {' · '}
            {request.employeeName || 'Employee'}
          </p>
        </div>

        <div className="text-right shrink-0">
          <div className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
            {t('outstation.tripDays')}
          </div>

          <div className="text-lg font-bold text-foreground">
            {tripDays || '—'}
          </div>
        </div>
      </div>

      {/* Sent back / rejected notices and owner actions */}
      {request.status === OTR_STATUS.SENT_BACK && (
        <div className="mb-6 p-4 rounded-2xl border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 text-sm flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <span className="font-bold">Sent back for changes.</span>{' '}
            {request.sentBackReason || 'Please review and resubmit.'}
            {isOwner && isExpenseStageSentBack(request.status, request.sentBackFrom) && (
              <div className="mt-1 text-[12px] text-[var(--color-text-secondary)]">
                Update your expenses in the Daily Expense Entry tab and submit them again.
              </div>
            )}
          </div>
          {isOwner && canEditTravelRequest(request.status, request.sentBackFrom) && (
            <button
              type="button"
              onClick={() => navigate(`/outstation-travel/${requestId}/edit`)}
              className="shrink-0 h-10 px-5 rounded-xl bg-[var(--color-primary)] text-white text-[12px] font-bold uppercase tracking-wider hover:bg-[var(--color-accent)] transition-all"
            >
              Edit &amp; Resubmit
            </button>
          )}
        </div>
      )}

      {request.status === OTR_STATUS.REJECTED && (
        <div className="mb-6 p-4 rounded-2xl border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 text-sm">
          <span className="font-bold text-[var(--color-error)]">Rejected.</span>{' '}
          {request.rejectReason || 'This request was rejected and can no longer be edited.'}
        </div>
      )}

      {request.status === OTR_STATUS.DRAFT && isOwner && (
        <div className="mb-6 p-4 rounded-2xl border border-[var(--color-border)] bg-white text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <span>This request is still a draft and has not been submitted.</span>
          <button
            type="button"
            onClick={() => navigate(`/outstation-travel/${requestId}/edit`)}
            className="shrink-0 h-10 px-5 rounded-xl bg-[var(--color-primary)] text-white text-[12px] font-bold uppercase tracking-wider hover:bg-[var(--color-accent)] transition-all"
          >
            Edit &amp; Submit
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap border-b border-[var(--color-text-border)]/65 bg-white rounded-t-2xl px-2">
        {OTR_TABS.map(tab => {
          const locked = EXPENSE_TABS.includes(tab.key) && !expensesUnlocked;
          return (
            <button
              key={tab.key}
              type="button"
              disabled={locked}
              title={locked ? EXPENSE_LOCK_MESSAGE : undefined}
              onClick={() => setActiveTab(tab.key)}
              className={cx(
                "py-3 px-5 text-sm font-bold border-b-2 transition -mb-[2px]",
                activeTab === tab.key
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-transparent text-[var(--color-text-secondary)] hover:text-foreground",
                locked && "opacity-50 cursor-not-allowed hover:text-[var(--color-text-secondary)]"
              )}
            >
              {locked ? '🔒 ' : ''}{t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-8 mt-6">

        {/* LEFT */}
        <div className="flex flex-col gap-6">

          {/* Travel Info */}
          {activeTab === 'travelInfo' && (
            <div className="p-4 sm:p-8 bg-white rounded-2xl sm:rounded-[2rem] border border-[var(--color-border)] shadow-sm">

              <h2 className="text-sm font-bold text-foreground mb-6 uppercase tracking-wider">
                {t('outstation.tripDetails')}
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4">

                <div>
                  <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                    {t('outstation.dateOfTravel')}
                  </label>

                  <div className="text-sm font-bold">
                    {formatOtrDate(request.dateOfTravel)}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                    {t('outstation.dateOfReturn')}
                  </label>

                  <div className="text-sm font-bold">
                    {formatOtrDate(request.dateOfReturn)}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                    {t('outstation.placeOfTravel')}
                  </label>

                  <div className="text-sm font-bold">
                    {request.placeOfTravel}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                    {t('outstation.purpose')}
                  </label>

                  <div className="text-sm font-bold">
                    {PURPOSE_I18N_KEYS[request.purpose] ? t(`outstation.purposeOptions.${PURPOSE_I18N_KEYS[request.purpose]}`) : request.purpose}
                  </div>
                </div>

                {request.purposeRecordName && (
                  <div className="col-span-2 md:col-span-3">
                    <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                      {t('outstation.customers')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {String(request.purposeRecordName).split(',').map((s) => s.trim()).filter(Boolean).map((name) => (
                        <span
                          key={name}
                          className="rounded-lg border border-[var(--color-text-border)] bg-white px-3 py-1 text-sm font-bold"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                    {t('outstation.modeOfTravel')}
                  </label>

                  <div className="text-sm font-bold">
                    {getModeOfTravelName(request)}
                  </div>
                </div>

                {/* Backend field: advanceRequest */}
                <div>
                  <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                    {t('outstation.advanceRequested')}
                  </label>

                  <div className="text-sm font-bold">
                    {request.advanceRequest != null
                      ? `₹${Number(
                          request.advanceRequest
                        ).toLocaleString()}`
                      : '—'}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                    {t('outstation.remarks')}
                  </label>

                  <div className="text-sm font-bold">
                    {request.description || '—'}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-[var(--color-text-secondary)] uppercase mb-1">
                    {t('outstation.cityTier')}{' '}
                    {permissions.canEditCityTier && (
                      <span className="text-[var(--color-error)]">
                        *
                      </span>
                    )}
                  </label>

                  {permissions.canEditCityTier ? (
                    <OTRSelect
                      value={request.tierId}
                      onChange={(v) => setRequest({ ...request, tierId: v ? Number(v) : null })}
                      options={[1, 2, 3].map(n => ({ value: n, label: t('outstation.tierN', { n }) }))}
                    />
                  ) : (
                    <div className="text-sm font-bold text-[var(--color-primary)]">
                      {request.tierName ||
                        (request.tierId
                          ? t('outstation.tierN', { n: request.tierId })
                          : 'Pending Assignment')}
                    </div>
                  )}

                  {permissions.canEditCityTier && (
                    <span className="text-[11px] text-[var(--color-text-secondary)] mt-1 block">
                      Choose the city tier. The matching Finance Travel Policy will be loaded and recalculated for the trip duration.
                    </span>
                  )}
                </div>

              </div>
            </div>
          )}

          {/* Tier budget (read-only) - visible to approvers before approval */}
          {activeTab === 'travelInfo' && (
            <div className="p-4 sm:p-8 bg-white rounded-2xl sm:rounded-[2rem] border border-[var(--color-border)] shadow-sm">
              <h2 className="text-sm font-bold text-foreground mb-2 uppercase tracking-wider">
                Tier Budget Details
              </h2>
              {policyHeads.length > 0 ? (
                <ExpenseHeadTable
                  policyHeads={getCompanyArrangeableHeads(policyHeads)}
                  userSelections={normalizeArrangedMap(request.companyArranged)}
                  isReadOnly={true}
                  emptyMessage="No expenses can be company arranged for this tier."
                />
              ) : (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Select a city tier above to load the corresponding Finance Travel Policy.
                </p>
              )}
            </div>
          )}

          {/* Daily Expense Entry */}
          {activeTab === 'dailyExpense' && expensesUnlocked && (
            <div className="p-4 sm:p-8 bg-white rounded-2xl sm:rounded-[2rem] border border-[var(--color-border)] shadow-sm">

              <DailyExpenseDashboard
                requestId={requestId}
                policyHeads={getEligibleExpenseHeads(
                  policyHeads,
                  request.companyArranged
                )}
                tripDays={tripDays}
                travelStartDate={request.dateOfTravel}
                advanceRequested={request.advanceRequest}
                currencySymbol={
                  request.currencySymbol || '₹'
                }
                canAddExpenses={
                  isOwner && canAddExpenseLines(request.status, request.sentBackFrom)
                }
                onClaimSubmitted={() =>
                  getTravelRequestById(requestId).then((d) =>
                    setRequest((prev) => ({ ...prev, ...d }))
                  )
                }
              />

            </div>
          )}

          {/* Approval */}
          {showApprovalBar && (
            <OTRApprovalActionBar
              onApprove={(remarks) =>
                handleAction(
                  approveTravelRequest,
                  remarks
                )
              }
              onReject={(remarks) =>
                handleAction(
                  rejectTravelRequest,
                  remarks
                )
              }
              onSendBack={(remarks) =>
                handleAction(
                  sendBackTravelRequest,
                  remarks
                )
              }
              isLoading={processing}
              error={actionError}
            />
          )}

        </div>

      </div>
    </AppLayout>
  );
};